import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  activeSchemaIdSelector,
  defaultLogOpsSelector,
  filterPrimitiveSelector,
} from '../../../../redux/selectors/builder';
import { IRowMeta, setFilterAction } from '../../../../redux/slices/oldBuilder';
import {
  FieldContext,
  IEventInput,
  IFilterFieldsFragment,
  PermissionKey,
  TagOption,
} from '../../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../../utils/interfaces';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { SvgIcon } from '../../../Icon';
import FilterWidget from '../../../FilterWidget';
import { IPredicateContext } from '../../../FilterWidget/types';
import { defaultEventInput } from '../../../../utils/builder';
import { ITooltipOption } from '../../../KubitTooltip/globalTooltips';
import KubitTooltip from '../../../KubitTooltip';
import Popover from '../../../Popover';
import { useActionsVisibilityContext } from '../../../ActionsVisibilityContext';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import { useDndEntityInternalContext } from '../../../DndEntity';
import { isNil } from 'lodash';
import { getDndActiveItemData } from '../../../DndEntity/utils';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import DropzoneArea from '../../../DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  IFieldEntityDropzoneDataType,
  INamedFilterEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';
import { createTagOptionInput } from '../../EntitySelection/utils';
import FieldDroppableZone from '../../../DndEntity/DndDroppableZone/FieldDroppableZone';
import usePopoverController from '../../../Popover/usePopoverController';

interface IProps {
  rowMeta: IRowMeta;
  eventsInput?: IEventInput[];
  fieldsDropzoneId?: string | null;
}

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const tooltipOption: ITooltipOption = {
  content:
    'Apply an event filter to a specific event for more granular analysis.',
};

const BuilderFilterFieldsTrigger = ({
  rowMeta,
  eventsInput,
  fieldsDropzoneId,
}: IProps) => {
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const dispatch = useAppDispatch();
  const { checkIsPermitted } = usePermission();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const isPermittedToReadField = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId: activeSchemaId,
  });

  const defaultLogOps = useAppSelector(defaultLogOpsSelector);

  const { hideActions } = useActionsVisibilityContext();

  const filterSelector = useMemo(filterPrimitiveSelector, []);
  const filter: MaybeNil<IFilterFieldsFragment> = useAppSelector((state) =>
    filterSelector(state, rowMeta),
  );

  const predicateContext: IPredicateContext = {
    fieldContext: FieldContext.Filter,
    events: eventsInput ?? [defaultEventInput],
    func: null,
  };

  const tags = [
    createTagOptionInput(TagOption.Field, {
      field: {
        func: predicateContext.func,
        context: predicateContext.fieldContext,
        events: predicateContext.events,
        arrayObjectContext: null,
      },
    }),
  ];

  const dndContext = useDndEntityInternalContext();

  const { defaultFilter, setDefaultFilter, popoverId, setPopoverId } =
    dndContext;

  const handleIconClick = useCallback(() => {
    hideActions();

    openPopover();
  }, [hideActions, openPopover]);

  const handleSubmitNewFilters = (newFilter: IFilterFieldsFragment) => {
    dispatch(
      setFilterAction({
        rowMeta,
        newFilter,
      }),
    );

    closeFilterWidget();
  };

  const closeFilterWidget = () => {
    closePopover();
    setPopoverId(null);
    setDefaultFilter(null);
  };

  // DndEntity context keeps track of which filter widget is open
  // by checking the popoverId against the generated fieldsDropzoneId
  // which would then open the filter widget on drop
  useEffect(() => {
    if (
      !isNil(popoverId) &&
      !isNil(fieldsDropzoneId) &&
      popoverId === fieldsDropzoneId
    ) {
      openPopover();
    }
  }, [popoverId, fieldsDropzoneId, openPopover]);

  const { activeItem } = useDndEntityInternalContext();

  const showAddFieldDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (!isNil(filter)) {
      return false;
    }

    return data && data.__typename === 'TagLeafField';
  }, [activeItem, filter]);

  const showAddFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (!isNil(filter)) {
      return false;
    }

    return data && data.__typename === 'TagLeafFilter';
  }, [activeItem, filter]);

  return (
    <>
      <Container ref={popoverAnchor}>
        <PermissionElementWrapper isPermitted={isPermittedToReadField}>
          <KubitTooltip
            {...tooltipOption}
            disabled={!isPermittedToReadField}
            noOpenDelay={true}
          >
            <SvgIcon
              name="filter"
              width={16}
              height={16}
              onClick={handleIconClick}
              isActive={isPopoverOpen}
              isHoverable
              isDisabled={!isPermittedToReadField}
            />
          </KubitTooltip>
        </PermissionElementWrapper>
      </Container>

      {showAddFieldDropzone && fieldsDropzoneId && (
        <FieldDroppableZone
          dropzoneId={fieldsDropzoneId}
          tags={tags}
          data={
            {
              id: fieldsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Fields,
              rowMeta,
            } satisfies IFieldEntityDropzoneDataType
          }
          hideOnDisabled
          shouldFitContent
        >
          <DropzoneArea size="small">+ Add Event Filter</DropzoneArea>
        </FieldDroppableZone>
      )}

      {showAddFilterDropzone && fieldsDropzoneId && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Filters]}
          id={fieldsDropzoneId}
          data={
            {
              id: fieldsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Filters,
              rowMeta,
            } satisfies INamedFilterEntityDropzoneDataType
          }
          shouldFitContent
        >
          <DropzoneArea size="small">+ Add Event Filter</DropzoneArea>
        </DndDroppableZone>
      )}

      {isPopoverOpen && (
        <Popover
          anchor={popoverAnchor.current}
          isOpen
          close={closeFilterWidget}
        >
          <FilterWidget
            onSubmit={handleSubmitNewFilters}
            schemaId={activeSchemaId}
            context={predicateContext}
            defaultFilter={defaultFilter ?? filter}
            defaultLogOps={defaultLogOps}
          />
        </Popover>
      )}
    </>
  );
};

export default BuilderFilterFieldsTrigger;
